import { useState } from "react";
import { useAccount } from "wagmi";
import { ArrowDownUp, Settings, Loader2, ExternalLink, Zap } from "lucide-react";
import { useSwap } from "../../hooks/useSwap";
import { TokenSelector } from "./TokenSelector";
import { formatTokenAmount, cn, explorerUrl } from "../../lib/utils";
import { formatUnits } from "viem";
import { ADDRESSES } from "../../config/contracts";
import { CL_FEE_TIERS, BIN_STEPS } from "../../config/v4Contracts";

const SLIPPAGE_OPTIONS = [50, 100, 200];

function tokenSymbolForAddress(address: string): string {
  const lower = address.toLowerCase();
  if (lower === ADDRESSES.WJASMY.toLowerCase()) return "WJASMY";
  return address.slice(0, 6) + "..." + address.slice(-4);
}

function RouteBadge({
  routeType,
  bestPool,
}: {
  routeType: "V2" | "CL" | "BIN";
  bestPool: ReturnType<typeof useSwap>["bestPool"];
}) {
  if (routeType === "V2") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface border border-border text-text-tertiary text-xs font-medium">
        V2 Pool
      </span>
    );
  }

  if (routeType === "CL") {
    const tier = CL_FEE_TIERS.find((t) => t.fee === bestPool?.fee);
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 border border-primary/25 text-primary text-xs font-medium">
        <Zap className="w-3 h-3" />
        CL {tier?.label ?? ""}
      </span>
    );
  }

  if (routeType === "BIN") {
    const step = BIN_STEPS.find((s) => s.binStep === bestPool?.binStep);
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 border border-primary/25 text-primary text-xs font-medium">
        <Zap className="w-3 h-3" />
        BIN {step?.label ?? ""}
      </span>
    );
  }

  return null;
}

export function SwapCard() {
  const { isConnected } = useAccount();
  const [showSettings, setShowSettings] = useState(false);
  const [customSlippage, setCustomSlippage] = useState("");

  const {
    state,
    balanceIn,
    balanceOut,
    quotedAmountOut,
    isQuoting,
    needsApproval,
    approve,
    isApproving,
    executeSwap,
    isSwapping,
    isConfirming,
    txHash,
    flipTokens,
    setTokenIn,
    setTokenOut,
    setAmountIn,
    setSlippage,
    insufficientBalance,
    priceImpact,
    path,
    routeType,
    bestPool,
    v4BetterBy,
  } = useSwap();

  const isLoading = isSwapping || isConfirming;
  const hasValidInput = state.amountIn !== "" && parseFloat(state.amountIn) > 0;
  const hasQuote = quotedAmountOut > 0n;
  const isV4Route = routeType === "CL" || routeType === "BIN";

  function handleSlippageChange(bps: number) {
    setSlippage(bps);
    setCustomSlippage("");
  }

  function handleCustomSlippage(value: string) {
    setCustomSlippage(value);
    const num = parseFloat(value);
    if (!isNaN(num) && num > 0 && num <= 50) {
      setSlippage(Math.round(num * 100));
    }
  }

  function getButtonText() {
    if (!isConnected) return "Connect Wallet";
    if (!hasValidInput) return "Enter Amount";
    if (insufficientBalance) return "Insufficient Balance";
    if (isQuoting) return "Fetching Quote...";
    if (!hasQuote && hasValidInput) return "No Liquidity";
    if (isApproving) return "Approving...";
    if (needsApproval) return `Approve ${state.tokenIn.symbol}`;
    if (isSwapping) return "Confirming...";
    if (isConfirming) return "Swapping...";
    return "Swap";
  }

  function handleAction() {
    if (needsApproval) {
      approve();
    } else {
      executeSwap();
    }
  }

  const rate =
    hasQuote && hasValidInput
      ? parseFloat(formatUnits(quotedAmountOut, state.tokenOut.decimals)) /
        parseFloat(state.amountIn)
      : 0;

  const priceImpactColor =
    priceImpact > 5
      ? "text-error"
      : priceImpact > 2
        ? "text-warning"
        : "text-text-secondary";

  return (
    <div className="glass-card p-6 w-full max-w-[480px] glow-primary">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display font-semibold text-xl">Swap</h2>
        <button
          onClick={() => setShowSettings(!showSettings)}
          className={cn(
            "p-2 rounded-lg transition-all",
            showSettings
              ? "bg-primary/15 text-primary"
              : "text-text-secondary hover:text-text-primary hover:bg-surface-hover"
          )}
        >
          <Settings className="w-5 h-5" />
        </button>
      </div>

      {showSettings && (
        <div className="mb-4 p-4 bg-surface/50 border border-border rounded-card animate-fade-in">
          <div className="text-sm text-text-secondary mb-3">Slippage Tolerance</div>
          <div className="flex items-center gap-2">
            {SLIPPAGE_OPTIONS.map((bps) => (
              <button
                key={bps}
                onClick={() => handleSlippageChange(bps)}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-sm font-medium transition-all border",
                  state.slippageBps === bps && !customSlippage
                    ? "bg-primary/15 text-primary border-primary/30"
                    : "bg-surface border-border text-text-secondary hover:text-text-primary"
                )}
              >
                {bps / 100}%
              </button>
            ))}
            <div className="relative flex-1">
              <input
                type="text"
                value={customSlippage}
                onChange={(e) => handleCustomSlippage(e.target.value)}
                placeholder="Custom"
                className="input-field text-sm py-1.5 pr-6"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary text-sm">
                %
              </span>
            </div>
          </div>
          {state.slippageBps > 500 && (
            <p className="text-xs text-warning mt-2">
              High slippage increases risk of front-running
            </p>
          )}
        </div>
      )}

      <div className="space-y-1 relative">
        <div className="token-input-row">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-text-tertiary">You Pay</span>
            <button
              onClick={() => {
                const maxBal = formatUnits(balanceIn, state.tokenIn.decimals);
                setAmountIn(maxBal);
              }}
              className="text-xs text-text-tertiary hover:text-primary transition-colors"
            >
              Balance: {formatTokenAmount(balanceIn, state.tokenIn.decimals)}
              <span className="ml-1 text-primary font-medium">MAX</span>
            </button>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="number"
              value={state.amountIn}
              onChange={(e) => setAmountIn(e.target.value)}
              placeholder="0.0"
              className="amount-input"
              min="0"
              step="any"
            />
            <TokenSelector
              selectedToken={state.tokenIn}
              onSelect={setTokenIn}
              otherToken={state.tokenOut}
            />
          </div>
        </div>

        <div className="flex justify-center -my-3 z-10 relative">
          <button
            onClick={flipTokens}
            className="w-10 h-10 rounded-xl bg-background border-4 border-background flex items-center justify-center hover:bg-surface-hover transition-all group"
          >
            <ArrowDownUp className="w-4 h-4 text-text-secondary group-hover:text-primary transition-colors" />
          </button>
        </div>

        <div className="token-input-row">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-text-tertiary">You Receive</span>
            <span className="text-xs text-text-tertiary">
              Balance: {formatTokenAmount(balanceOut, state.tokenOut.decimals)}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="number"
              value={state.amountOut}
              placeholder="0.0"
              className="amount-input"
              readOnly
            />
            <TokenSelector
              selectedToken={state.tokenOut}
              onSelect={setTokenOut}
              otherToken={state.tokenIn}
            />
          </div>
        </div>
      </div>

      {/* Route badge row */}
      {hasValidInput && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-text-tertiary">Route:</span>
          <RouteBadge routeType={routeType} bestPool={bestPool} />
          {isV4Route && v4BetterBy > 0n && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/25 text-green-400 text-xs font-medium">
              <Zap className="w-3 h-3" />
              Better price via V4
            </span>
          )}
        </div>
      )}

      {hasQuote && hasValidInput && (
        <div className="mt-3 p-3 bg-surface/30 rounded-button border border-border text-sm">
          <div className="flex items-center justify-between text-text-secondary">
            <span>Rate</span>
            <span>
              1 {state.tokenIn.symbol} = {rate.toFixed(6)} {state.tokenOut.symbol}
            </span>
          </div>
          {routeType === "V2" && (
            <div className="flex items-center justify-between text-text-secondary mt-1">
              <span>Price Impact</span>
              <span className={priceImpactColor}>
                {priceImpact < 0.01 ? "<0.01" : priceImpact.toFixed(2)}%
              </span>
            </div>
          )}
          <div className="flex items-center justify-between mt-1">
            <span className="text-text-secondary">Protocol Fee</span>
            <span className="text-green-400 font-semibold">0%</span>
          </div>
          <div className="flex items-center justify-between text-text-secondary mt-1">
            <span>Slippage</span>
            <span>{state.slippageBps / 100}%</span>
          </div>
          <div className="flex items-center justify-between text-text-secondary mt-1">
            <span>Min. Received</span>
            <span>
              {formatTokenAmount(
                (quotedAmountOut * BigInt(10000 - state.slippageBps)) / 10000n,
                state.tokenOut.decimals
              )}{" "}
              {state.tokenOut.symbol}
            </span>
          </div>
          {routeType === "V2" && path.length > 0 && (
            <div className="flex items-center justify-between text-text-secondary mt-1">
              <span>Route</span>
              <span className="text-xs">
                {state.tokenIn.symbol}
                {path.length > 2 &&
                  path.slice(1, -1).map((addr, i) => (
                    <span key={i}>
                      {" -> "}
                      {tokenSymbolForAddress(addr)}
                    </span>
                  ))}
                {" -> "}
                {state.tokenOut.symbol}
              </span>
            </div>
          )}
        </div>
      )}

      {priceImpact > 10 && hasQuote && hasValidInput && routeType === "V2" && (
        <div className="mt-2 p-2 bg-error/10 border border-error/20 rounded-button text-xs text-error text-center">
          Warning: High price impact. You may receive significantly fewer tokens.
        </div>
      )}

      <button
        onClick={handleAction}
        disabled={
          !isConnected ||
          !hasValidInput ||
          insufficientBalance ||
          (!hasQuote && hasValidInput && !isQuoting) ||
          isLoading ||
          isApproving
        }
        className="btn-primary w-full mt-4 flex items-center justify-center gap-2"
      >
        {(isLoading || isApproving || isQuoting) && (
          <Loader2 className="w-4 h-4 animate-spin" />
        )}
        {getButtonText()}
      </button>

      {txHash && (
        <div className="mt-3 text-center">
          <a
            href={explorerUrl("tx", txHash)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary-hover transition-colors"
          >
            View Transaction <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}
    </div>
  );
}
